// Subdomain split for the "dashboard-only" host (yours.innernet.live).
//
// Everything here is GATED by env vars so the code is INERT until we flip it on
// in Vercel — deploying it changes nothing until both vars are set:
//
//   NEXT_PUBLIC_DASHBOARD_HOST = yours.innernet.live   → enables host routing
//   NEXT_PUBLIC_COOKIE_DOMAIN  = .innernet.live         → shares the session
//                                                          cookie across apex
//                                                          + subdomain
//
// Activation is a one-time coordinated step: setting the cookie domain logs
// every existing session out once (host-only cookies stop matching), so flip
// it deliberately, after yours.innernet.live serves in Vercel.

export const DASHBOARD_HOST = process.env.NEXT_PUBLIC_DASHBOARD_HOST ?? ''
export const COOKIE_DOMAIN = process.env.NEXT_PUBLIC_COOKIE_DOMAIN ?? ''

/** Route prefixes that make up the logged-in app — these live on the dashboard
 *  subdomain. Everything else (/, /docs, /login, /privacy, …) stays on apex. */
export const APP_PREFIXES = [
  '/dashboard',
  '/projects',
  '/account',
  '/me',
  '/connectors',
  '/admin',
  '/ours',
] as const

/** Paths INSIDE an app prefix that a signed-out person may still reach. An
 *  invite is a door: you have to be able to stand at it and look in before you
 *  decide to sign in and step through. `/ours/join/<token>` shows only what the
 *  token entitles (preview_space_invite, migration 067) and every action on it
 *  still requires a session. */
export const PUBLIC_APP_PATHS = ['/ours/join/'] as const

/** EXACT paths that sit inside an app prefix but are public MARKETING pages,
 *  not app routes. `/ours` is the public door for the shared side — a stranger
 *  has to be able to read what a room is and join the waitlist. `/ours/<slug>`
 *  is a real room and stays an app route, gated and on the dashboard host.
 *
 *  This list is exact-match ONLY, on purpose: a prefix here would open every
 *  room underneath it. */
export const PUBLIC_APP_EXACT = ['/ours'] as const

export function isPublicAppPath(pathname: string): boolean {
  return PUBLIC_APP_PATHS.some((p) => pathname.startsWith(p))
}

export function isAppPath(pathname: string): boolean {
  // a public marketing page inside an app prefix is not an app path at all —
  // it belongs on the apex, signed out, like /docs.
  if ((PUBLIC_APP_EXACT as readonly string[]).includes(pathname)) return false
  return APP_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'))
}

/** Host header without the port, lowercased. */
export function hostOf(request: { headers: { get(name: string): string | null } }): string {
  return (request.headers.get('host') ?? '').split(':')[0].toLowerCase()
}

/** Cookie options for the Supabase SSR clients. Returns the shared-domain
 *  option only when NEXT_PUBLIC_COOKIE_DOMAIN is set; otherwise undefined,
 *  preserving the current host-only cookie behavior. */
export function supabaseCookieOptions(): { domain: string } | undefined {
  return COOKIE_DOMAIN ? { domain: COOKIE_DOMAIN } : undefined
}
